import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  IndianRupee,
  Loader2,
  ShieldCheck,
  Sparkles,
  QrCode,
  Wallet,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import MembershipCardPreview from "@/components/MembershipCardPreview";
import { api, errMessage } from "@/lib/api";
import { formatMobile, validateMobile, validateName, MEMBERSHIP_PRICE } from "@/lib/membership";

export default function AgentNewCard() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [age, setAge] = useState("");
  const [emrStation, setEmrStation] = useState("");
  const [mobile, setMobile] = useState("");
  const [whatsappOptIn, setWhatsappOptIn] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [qr, setQr] = useState(null);

  useEffect(() => { refreshUser().catch(() => {}); }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const commissionPreview = useMemo(
    () => (MEMBERSHIP_PRICE * (user?.commission_rate || 0)) / 100,
    [user]
  );

  const onDetails = (e) => {
    e.preventDefault();
    if (!validateName(name)) return toast.error("Enter a valid patient name");
    if (!gender) return toast.error("Please select gender");
    if (!age || parseInt(age) < 1) return toast.error("Please enter a valid age");
    if (!validateMobile(mobile)) return toast.error("Enter a valid 10-digit mobile");
    setStep(2);
  };

  const onPay = async () => {
    if (processing) return;
    setProcessing(true);
    try {
      const res = await api.post("/agent/create-qr", {
        patient_name: name.trim(),
        patient_mobile: mobile.replace(/\D/g, ""),
        whatsapp_optin: whatsappOptIn,
        gender,
        age: parseInt(age),
        emr_station: emrStation.trim() || undefined,
      });
      setQr(res.data);
    } catch (err) {
      toast.error(errMessage(err, "Could not start payment. Try again."));
      setProcessing(false);
    }
  };

  // Wallet payment — Razorpay Checkout popup (UPI/Card/Netbanking/Wallets).
  const onPayCheckout = async () => {
    if (processing) return;
    setProcessing(true);
    try {
      const orderRes = await api.post("/agent/create-order", {
        patient_name: name.trim(),
        patient_mobile: mobile.replace(/\D/g, ""),
        whatsapp_optin: whatsappOptIn,
        gender,
        age: parseInt(age),
        emr_station: emrStation.trim() || undefined,
      });
      const order = orderRes.data;
      await new Promise((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: order.key_id,
          order_id: order.order_id,
          amount: order.amount,
          currency: order.currency || "INR",
          name: "1Rupee Clinic",
          description: "Annual Membership",
          prefill: { name: order.patient_name, contact: "+91" + order.patient_mobile },
          theme: { color: "#4f46e5" },
          handler: async (response) => {
            try {
              const verifyRes = await api.post("/agent/verify-payment", {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              resolve();
              navigate("/agent/card-success", { state: { membership: verifyRes.data } });
            } catch (err) {
              toast.error(errMessage(err, "Payment verification failed"));
              reject(err);
            } finally {
              setProcessing(false);
            }
          },
          modal: { ondismiss: () => { setProcessing(false); resolve(); } },
        });
        rzp.open();
      });
    } catch (err) {
      toast.error(errMessage(err, "Could not initiate payment. Try again."));
      setProcessing(false);
    }
  };

  const cancelQr = () => {
    setQr(null);
    setProcessing(false);
  };

  // Poll QR payment status while the QR is shown. On payment -> card-success.
  useEffect(() => {
    if (!qr?.qr_id) return;
    let active = true;
    const startedAt = Date.now();
    const iv = setInterval(async () => {
      if (!active) return;
      if (Date.now() - startedAt > 8 * 60 * 1000) {
        clearInterval(iv);
        if (active) {
          toast.error("QR expired. Please try again.");
          setQr(null);
          setProcessing(false);
        }
        return;
      }
      try {
        const res = await api.post("/agent/qr-status", { qr_id: qr.qr_id });
        if (res.data?.paid) {
          active = false;
          clearInterval(iv);
          navigate("/agent/card-success", { state: { membership: res.data } });
        }
      } catch {
        /* transient — keep polling */
      }
    }, 4000);
    return () => {
      active = false;
      clearInterval(iv);
    };
  }, [qr, navigate]);

  return (
    <DashboardLayout>
      {qr && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-2xl shadow-elegant max-w-md w-full p-5 text-center max-h-[88vh] overflow-y-auto">
            <h2 className="font-display text-lg font-bold text-foreground mb-3">
              Scan &amp; Pay ₹{MEMBERSHIP_PRICE}
            </h2>
            <img
              src={qr.qr_image_url}
              alt="UPI QR code"
              className="mx-auto w-[300px] max-w-full h-auto rounded-lg bg-white"
            />
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Waiting for payment…
            </div>
            <Button variant="outline" className="mt-4 w-full rounded-xl" onClick={cancelQr}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <Link
            to="/agent"
            className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground mb-2"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
          </Link>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
            Sell a new membership
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Collect ₹{MEMBERSHIP_PRICE}, generate the patient card and we'll WhatsApp it to them instantly.
          </p>
        </div>
        <Badge className="hidden sm:inline-flex bg-success/12 text-success border border-success/25 hover:bg-success/12">
          You earn ₹{commissionPreview.toFixed(2)} on success
        </Badge>
      </div>

      {/* Stepper */}
      <ol className="flex items-center gap-2 sm:gap-4 mb-6">
        {[
          { id: 1, label: "Patient details" },
          { id: 2, label: "Collect payment" },
        ].map((s, i, arr) => {
          const active = step === s.id;
          const done = step > s.id;
          return (
            <li key={s.id} className="flex items-center gap-2 sm:gap-4">
              <div
                className={
                  "flex items-center gap-2.5 " +
                  (done || active ? "text-foreground" : "text-muted-foreground")
                }
              >
                <span
                  className={
                    "inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold border transition-all " +
                    (done
                      ? "bg-success text-success-foreground border-success"
                      : active
                      ? "bg-gradient-primary text-primary-foreground border-transparent shadow-elegant"
                      : "bg-background text-muted-foreground border-border")
                  }
                >
                  {done ? <CheckCircle2 className="h-4 w-4" /> : s.id}
                </span>
                <span className="text-xs sm:text-sm font-medium hidden sm:inline">
                  {s.label}
                </span>
              </div>
              {i < arr.length - 1 && (
                <span
                  className={"h-px w-8 sm:w-16 " + (done ? "bg-success" : "bg-border")}
                />
              )}
            </li>
          );
        })}
      </ol>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          {/* Step 1 — patient details */}
          {step === 1 && (
            <Card className="border-border/70 shadow-soft">
              <CardContent className="p-6 sm:p-8">
                <h2 className="font-display text-xl font-bold text-foreground">
                  Patient details
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Card will be issued under the patient's name and tagged to your agent ID.
                </p>
                <form onSubmit={onDetails} className="mt-6 space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="name">Patient full name</Label>
                    <Input
                      id="name"
                      placeholder="e.g. Priya Sharma"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mobile">Patient WhatsApp mobile</Label>
                    <div className="flex items-stretch rounded-md border border-input bg-background overflow-hidden focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background">
                      <span className="flex items-center px-3 text-sm font-medium bg-muted/60 text-muted-foreground border-r border-border">
                        +91
                      </span>
                      <Input
                        id="mobile"
                        inputMode="numeric"
                        placeholder="98765 43210"
                        value={formatMobile(mobile)}
                        onChange={(e) =>
                          setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                        }
                        className="h-11 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none bg-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Gender *</Label>
                      <Select value={gender} onValueChange={setGender}>
                        <SelectTrigger className="h-11">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Male</SelectItem>
                          <SelectItem value="2">Female</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="age">Age *</Label>
                      <Input
                        id="age"
                        type="text"
                        inputMode="numeric"
                        placeholder="e.g. 35"
                        value={age}
                        onChange={(e) => setAge(e.target.value.replace(/\D/g, "").slice(0, 3))}
                        className="h-11"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="emr_station">EMR Station <span className="text-muted-foreground font-normal">(Optional)</span></Label>
                    <Input
                      id="emr_station"
                      placeholder="e.g. Andheri, Kandivali"
                      value={emrStation}
                      onChange={(e) => setEmrStation(e.target.value)}
                      maxLength={50}
                      className="h-11"
                    />
                  </div>

                  {/* WhatsApp opt-in hidden — defaults to opted-in.
                  <label className="flex items-start gap-3 rounded-xl border border-border/70 bg-muted/40 p-3.5 cursor-pointer hover:bg-muted/70 transition-colors">
                    <Checkbox
                      checked={whatsappOptIn}
                      onCheckedChange={(v) => setWhatsappOptIn(Boolean(v))}
                      className="mt-0.5"
                    />
                    <span className="text-sm text-foreground">
                      Send the card image to patient on WhatsApp
                      <span className="block text-xs text-muted-foreground mt-0.5">
                        Recommended — patient will have it on their phone instantly.
                      </span>
                    </span>
                  </label>
                  */}

                  <div className="pt-1 flex items-center justify-between">
                    <div className="text-sm">
                      <div className="font-semibold text-foreground">Collect ₹{MEMBERSHIP_PRICE}.00</div>
                      <div className="text-xs text-muted-foreground">
                        You earn +₹{commissionPreview.toFixed(2)} ({user?.commission_rate}%)
                      </div>
                    </div>
                    <Button
                      type="submit"
                      size="lg"
                      className="h-11 bg-gradient-primary text-primary-foreground rounded-xl shadow-elegant"
                    >
                      Proceed to payment <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Step 2 — online payment */}
          {step === 2 && (
            <Card className="border-border/70 shadow-soft">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-display text-xl font-bold text-foreground">
                      Collect payment
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Powered by Razorpay · PCI-DSS Level 1
                    </p>
                  </div>
                </div>

                <div className="mt-5 rounded-2xl bg-gradient-primary text-primary-foreground p-5 flex items-center justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] opacity-80">
                      Amount
                    </div>
                    <div className="flex items-end gap-1 mt-1">
                      <IndianRupee className="h-7 w-7 mb-1" />
                      <span className="font-display font-bold text-4xl leading-none">{MEMBERSHIP_PRICE}</span>
                      <span className="text-sm opacity-80 mb-1">.00</span>
                    </div>
                    <div className="text-xs opacity-80 mt-1">Membership for {name}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] uppercase tracking-[0.22em] opacity-80">
                      Your commission
                    </div>
                    <div className="font-display font-bold text-2xl mt-1">
                      +₹{commissionPreview.toFixed(2)}
                    </div>
                    <div className="text-xs opacity-80">at {user?.commission_rate}%</div>
                  </div>
                </div>

                {/* Static "Online Payment / UPI, Card, Netbanking & Wallets via Razorpay" card hidden.
                <div className="mt-6">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-3">
                    Payment method
                  </div>
                  <div className="rounded-xl border border-primary bg-primary/5 ring-2 ring-primary/20 p-4 flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-primary text-primary-foreground">
                      <IndianRupee className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-foreground">Online Payment</div>
                      <div className="text-xs text-muted-foreground">
                        UPI, Card, Netbanking & Wallets via Razorpay
                      </div>
                    </div>
                    <span className="ml-auto h-4 w-4 rounded-full border-2 border-primary bg-primary" />
                  </div>
                </div>
                */}

                <div className="mt-6 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-3">
                  Payment method
                </div>
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={onPay}
                    disabled={processing}
                    className="w-full flex items-center gap-3 rounded-xl border-2 border-primary/40 bg-primary/5 hover:bg-primary/10 p-4 text-left transition disabled:opacity-60"
                  >
                    <QrCode className="h-6 w-6 text-primary shrink-0" />
                    <span className="flex-1">
                      <span className="block font-semibold text-foreground">Online Payment (QR)</span>
                      <span className="block text-xs text-muted-foreground">
                        Scan &amp; pay ₹{MEMBERSHIP_PRICE} with any UPI app
                      </span>
                    </span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                  <button
                    type="button"
                    onClick={onPayCheckout}
                    disabled={processing}
                    className="w-full flex items-center gap-3 rounded-xl border-2 border-border hover:bg-muted/40 p-4 text-left transition disabled:opacity-60"
                  >
                    <Wallet className="h-6 w-6 text-primary shrink-0" />
                    <span className="flex-1">
                      <span className="block font-semibold text-foreground">Wallet Payment</span>
                      <span className="block text-xs text-muted-foreground">
                        UPI, Card, Netbanking &amp; Wallets via Razorpay
                      </span>
                    </span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                  <div className="flex items-center justify-between pt-1">
                    <Button variant="ghost" size="sm" onClick={() => setStep(1)} disabled={processing}>
                      <ArrowLeft className="h-4 w-4" /> Edit details
                    </Button>
                    {processing && (
                      <span className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Processing…
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <ShieldCheck className="h-3.5 w-3.5 text-success" /> Sale is tagged to your
                  agent ID for commission tracking.
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar live preview */}
        <aside className="lg:col-span-2 space-y-5">
          <div className="rounded-2xl border border-border/70 bg-card p-5 shadow-soft">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-3">
              Order summary
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-foreground">Annual Membership</span>
              <span className="font-semibold text-foreground">₹{MEMBERSHIP_PRICE}.00</span>
            </div>
            <Separator className="my-3" />
            <div className="flex items-center justify-between text-base font-semibold">
              <span>Total</span>
              <span className="text-primary">₹{MEMBERSHIP_PRICE}.00</span>
            </div>
            <div className="mt-3 rounded-lg bg-success/8 border border-success/20 p-3 text-xs text-success-foreground">
              <span className="text-success font-semibold">
                +₹{commissionPreview.toFixed(2)}
              </span>{" "}
              commission to{" "}
              <span className="text-foreground font-medium">{user?.name}</span> (
              {user?.commission_rate}%).
            </div>
          </div>

          <div className="rounded-2xl border border-dashed border-primary/30 bg-background/60 p-5">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-3">
              Live card preview
            </div>
            <MembershipCardPreview
              name={name || "Patient name"}
              mobile={mobile || "9999999999"}
              membershipNumber="XXXXXXXX"
              issued="—"
              expires="—"
            />
          </div>
        </aside>
      </div>
    </DashboardLayout>
  );
}
